// The after-apply callout of an attached deployment (spec 11 6.15.15): after a completed apply, the connect run it
// queued, live until that run ends, then connected or its failure with Retry or the Ask sentence.
import { useQueryClient } from '@tanstack/react-query';
import { CircleCheck, CircleX, Clock, Network as NetworkIcon } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { useApi } from '../../api/context';
import { NETWORK_POLL_MS, useNetworkActiveRuns, useRecentRuns } from '../../api/queries';
import type { Deployment, Run } from '../../api/types';
import { isTerminalRunStatus } from '../../api/types';
import { useToast } from '../../components/toast';
import { ActionButton } from '../../components/ui';
import { strings } from '../../strings';
import { PlanNetworkButton } from '../deployment/NetworkProblem';
import { isGrantPending, networkErrorInfo, useManageNetworks } from '../shared';
import { connectRunAfter, retryWords } from './runHelpers';

const r = strings.networks.runs;

export function AfterApplyCallout({ run, deployment }: { run: Run; deployment: Deployment }) {
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const admin = useManageNetworks();
  const network = deployment.network!;
  const [polling, setPolling] = useState(true);
  const runs = useRecentRuns(deployment.id, { refetchInterval: polling ? NETWORK_POLL_MS : false });
  const connect = connectRunAfter(runs.data?.items ?? [], deployment.id, run);
  const live = !connect || !isTerminalRunStatus(connect.status);
  if (polling !== live) setPolling(live);
  const active = useNetworkActiveRuns(network.id, live && connect ? NETWORK_POLL_MS : false);
  const [retrying, setRetrying] = useState(false);
  const dev = deployment.name;
  if (!connect) return null;

  const open = (
    <Link className="btn btn-sm" to={`/runs/${connect.id}`}>
      {r.open}
    </Link>
  );

  if (connect.status === 'completed')
    return (
      <p role="status" className="callout callout-success" data-testid="after-apply-callout">
        <CircleCheck aria-hidden className="size-4 shrink-0" />
        <span className="min-w-0 flex-1">{r.connected(network.name)}</span>
        {open}
      </p>
    );

  if (isGrantPending(connect)) {
    const pending = network.attachments.find((a) => a.waitingFor?.reason === 'not-before')?.waitingFor?.until;
    return (
      <p role="status" className="callout callout-info" data-testid="after-apply-callout">
        <Clock aria-hidden className="size-4 shrink-0" />
        <span className="min-w-0 flex-1">
          {r.applied(dev, network.name)} · {retryWords(pending)}
        </span>
        {open}
      </p>
    );
  }

  if (connect.status === 'failed' || connect.status === 'canceled') {
    const info = connect.error
      ? networkErrorInfo(connect.error.code, { network: network.name, deployment: dev })
      : null;
    return (
      <div role="alert" className="callout callout-danger" data-testid="after-apply-callout">
        <CircleX aria-hidden className="size-4 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{r.connectFailed(network.name)}</p>
          {connect.error && (
            <p>
              {info?.sentence ?? connect.error.message}{' '}
              <code className="font-mono text-xs">{connect.error.code}</code>
            </p>
          )}
        </div>
        <span className="flex shrink-0 flex-wrap gap-2">
          {info?.needsNetworkApply ? (
            admin ? (
              <PlanNetworkButton networkId={network.id} />
            ) : (
              <span className="text-[12.5px]">{strings.networks.askAdminToApply(network.name)}</span>
            )
          ) : (
            <ActionButton
              permission="operate"
              className="btn-sm"
              disabled={retrying}
              onClick={async () => {
                setRetrying(true);
                try {
                  await api.connectAttachment(network.id, deployment.id);
                  toast.show({ tone: 'success', title: r.retryQueued });
                  setPolling(true);
                  void queryClient.invalidateQueries({ queryKey: ['runs'] });
                  void queryClient.invalidateQueries({ queryKey: ['deployment', deployment.id] });
                } catch (error) {
                  toast.error(error);
                } finally {
                  setRetrying(false);
                }
              }}
            >
              {r.retry}
            </ActionButton>
          )}
          {open}
        </span>
      </div>
    );
  }

  const ahead = (active.data?.items ?? []).filter(
    (x) => x.id !== connect.id && Date.parse(x.createdAt) < Date.parse(connect.createdAt),
  ).length;
  return (
    <p role="status" className="callout callout-info" data-testid="after-apply-callout">
      <NetworkIcon aria-hidden className="size-4 shrink-0" />
      <span className="min-w-0 flex-1">
        {r.applied(dev, network.name)} ·{' '}
        {connect.status === 'running'
          ? r.connectRunning
          : ahead > 0
            ? r.queuedBehind(ahead)
            : r.connectQueued}
      </span>
      {open}
    </p>
  );
}
